import * as React from 'react';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { WorkspaceCard } from '../WorkspaceCard';
import { SettingsTeamMember, SettingsWorkspace } from '../../../models/settings-pages';
import { GetOrganizationLazyQueryHookResult } from '../../../models/graphql';
import { NewWorkspaceModal } from '../NewWorkspaceModal';
import { DeleteWorkspaceModal } from '../DeleteWorkspaceModal';
import { Modal } from '../../../components/containers/Modal';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';

export type WorkspacesViewProps = {
  workspaces: SettingsWorkspace[] | undefined;
  reachedWorkspaceLimit: boolean;
  organizationResults: GetOrganizationLazyQueryHookResult[1];
  openNewWorkspaceModal: boolean;
  setOpenNewWorkspaceModal: (openNewWorkspaceModal: boolean) => void;
  openDeleteWorkspaceModal: boolean;
  setOpenDeleteWorkspaceModal: (openDeleteWorkspaceModal: boolean) => void;
  allTeamMembers: SettingsTeamMember[] | undefined;
  modalWorkspace: SettingsWorkspace | null;
  openAddWorkspaceModal: () => void;
  openEditWorkspaceModal: (workspace: SettingsWorkspace) => void;
  openWorkspaceDeleteModal: (workspace: SettingsWorkspace) => void;
  openDeleteWorkspaceWarningModal: boolean;
  setOpenDeleteWorkspaceWarningModal: (open: boolean) => void;
};

export const WorkspacesView = (props: WorkspacesViewProps): JSX.Element => {
  const workspaceCount = props.workspaces?.length || 0;
  const workspaceCards = props.workspaces?.map(workspace => {
    return (
      <WorkspaceCard
        key={`ws-card-${workspace.id}`}
        workspace={workspace}
        organizationMembers={props.allTeamMembers || []}
        editWorkspaceAction={() => props.openEditWorkspaceModal(workspace)}
        deleteWorkspaceAction={() => {
          workspace.isDeletable
            ? props.openWorkspaceDeleteModal(workspace)
            : props.setOpenDeleteWorkspaceWarningModal(true);
        }}
        isLastWorkspace={workspaceCount < 2 || false}
      />
    );
  });

  const headerActionButton: JSX.Element = (
    <>
      <div className={'hide-for-small show-for-tablet-portrait'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.secondary}
          icon={{ side: 'left', value: IconsEnum.plus }}
          action={() => props.openAddWorkspaceModal()}
          disabled={props.reachedWorkspaceLimit}>
          Add a Workspace
        </Button>
      </div>
      <div className={'show-for-small hide-for-tablet-portrait'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.secondary}
          icon={{ side: 'center', value: IconsEnum.plus }}
          action={() => props.openAddWorkspaceModal()}
        />
      </div>
    </>
  );

  return (
    <CenteredContentLayout
      loading={props.organizationResults.loading}
      headerTitle={'Workspaces'}
      hasSidebar
      headerActionButton={headerActionButton}>
      <div className={'cell small-12'}>{workspaceCards}</div>
      {props.openNewWorkspaceModal && (
        <NewWorkspaceModal
          members={props.allTeamMembers}
          onClose={() => props.setOpenNewWorkspaceModal(false)}
          workspace={props.modalWorkspace}
        />
      )}
      {props.openDeleteWorkspaceModal && (
        <DeleteWorkspaceModal
          onClose={() => props.setOpenDeleteWorkspaceModal(false)}
          workspace={props.modalWorkspace}
        />
      )}
      {props.openDeleteWorkspaceWarningModal && (
        <Modal
          title={"Can't delete Workspace"}
          onClose={() => props.setOpenDeleteWorkspaceWarningModal(false)}
          showCloseIcon={false}
          primaryButtonTitle={'Got it!'}
          primaryButtonAction={() => props.setOpenDeleteWorkspaceWarningModal(false)}>
          <div>
            The Workspace you are trying to delete is the only Workspace assigned to at least one member of your
            Organization. Please make sure they are assigned to a different Workspace before deleting this one.
          </div>
        </Modal>
      )}
    </CenteredContentLayout>
  );
};
